import React from 'react';
import {
  Badge,
  Empty,
  List,
  Popover,
  Space,
  Tag,
  Typography
} from 'antd';
import { Button } from '../../design-system';
import {
  BellOutlined,
  RightOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { useTheme } from '../../providers/theme';
import { getColors } from '../utils/getColors';
import { useNotificationCenter } from '../../providers/NotificationCenter';
import {
  NotificationCenterItem,
  NotificationSeverity
} from '../../providers/NotificationCenter';

const severityColors: Record<NotificationSeverity, string> = {
  info: 'blue',
  warning: 'gold',
  error: 'red'
};

const triggerButtonStyle = {
  background: 'transparent',
  boxShadow: 'none'
} as const;

interface NotificationCenterButtonProps {
  placement?: 'bottomRight' | 'rightBottom' | 'right';
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
}

const NotificationCenterButton = ({
  placement = 'bottomRight',
  triggerClassName = 'buttons-topnav',
  triggerStyle,
  iconStyle,
}: NotificationCenterButtonProps = {}) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { notifications, isOpen, setIsOpen, navigateToTarget } =
    useNotificationCenter();
  const [expandedNotificationIds, setExpandedNotificationIds] = useState<
    string[]
  >([]);

  const activeCount = notifications.length;

  const toggleExpanded = (notificationId: string) => {
    setExpandedNotificationIds(current =>
      current.includes(notificationId)
        ? current.filter(id => id !== notificationId)
        : [...current, notificationId]
    );
  };

  const content = useMemo(
    () => (
      <div
        style={{
          width: 420,
          maxWidth: 'calc(100vw - 2rem)'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem'
          }}
        >
          <Typography.Text
            strong
            style={{ color: colors.text, fontSize: '14px' }}
          >
            Notifications
          </Typography.Text>
          <Tag color={activeCount > 0 ? 'processing' : 'default'}>
            {activeCount} active
          </Tag>
        </div>
        {notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No active notifications"
          />
        ) : (
          <List
            dataSource={notifications}
            split
            style={{ maxHeight: 420, overflowY: 'auto', paddingRight: '0.25rem' }}
            renderItem={(item: NotificationCenterItem) => {
              const isExpanded = expandedNotificationIds.includes(item.id);
              const canExpand = (item.children?.length ?? 0) > 0;

              return (
                <List.Item style={{ display: 'block', padding: '0.75rem 0' }}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <Badge
                      status={
                        item.severity === 'warning'
                          ? 'warning'
                          : item.severity === 'error'
                            ? 'error'
                            : 'processing'
                      }
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '0.75rem'
                        }}
                      >
                        <div>
                          <Typography.Text
                            strong
                            style={{ display: 'block', color: colors.text }}
                          >
                            {item.title}
                          </Typography.Text>
                          <Typography.Text
                            style={{
                              display: 'block',
                              color: colors.text,
                              opacity: 0.8
                            }}
                          >
                            {item.message}
                          </Typography.Text>
                        </div>
                        <Tag color={severityColors[item.severity]}>
                          {item.severity}
                        </Tag>
                      </div>
                      <Space size="small" style={{ marginTop: '0.5rem' }}>
                        {canExpand ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            startIcon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                            onClick={() => toggleExpanded(item.id)}
                            style={{ paddingInline: 0, color: colors.colorPrimary }}
                          >
                            {isExpanded ? 'Hide samples' : 'Click for more'}
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigateToTarget(item.target)}
                          style={{ paddingInline: 0, color: colors.colorPrimary }}
                        >
                          Open location
                        </Button>
                      </Space>
                      {canExpand && isExpanded ? (
                        <List
                          size="small"
                          dataSource={item.children}
                          style={{
                            marginTop: '0.5rem',
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            padding: '0.25rem 0.5rem',
                            backgroundColor: colors.projectsBg
                          }}
                          renderItem={child => (
                            <List.Item
                              style={{
                                padding: '0.35rem 0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '0.5rem'
                              }}
                            >
                              <Typography.Text style={{ color: colors.text }}>
                                {child.label}
                              </Typography.Text>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigateToTarget(child.target)}
                                style={{
                                  paddingInline: 0,
                                  color: colors.colorPrimary
                                }}
                              >
                                Open refinement
                              </Button>
                            </List.Item>
                          )}
                        />
                      ) : null}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    ),
    [
      activeCount,
      colors.border,
      colors.colorPrimary,
      colors.projectsBg,
      colors.text,
      expandedNotificationIds,
      navigateToTarget,
      notifications
    ]
  );

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement={placement}
      overlayInnerStyle={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 12
      }}
    >
      <div
        className={triggerClassName}
        title="Open notifications"
        style={triggerStyle}
      >
        <Badge count={activeCount} size="small" offset={[-2, 2]}>
          <Button
            variant="ghost"
            aria-label="Open notifications"
            startIcon={<BellOutlined style={{ fontSize: '1.2rem', ...iconStyle }} />}
            style={triggerButtonStyle}
          />
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationCenterButton;
